import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CirclePlay, Database, Video, Pointer, PenTool, Newspaper, Volleyball, ArrowRight, ChevronDown } from 'lucide-react';
import { Project } from '../data/projectsData';
import '../styles/MinimalProjectCard.css';

interface MinimalProjectCardProps {
  project: Project;
}

const MinimalProjectCard: React.FC<MinimalProjectCardProps> = ({ project }) => {
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();

  const handleCardClick = () => {
    navigate(`/work/${project.slug}`);
  };

  const categoryIcons = {
    automation: CirclePlay,
    data: Database,
    ar: Video,
    touch: Pointer,
    branding: PenTool,
    news: Newspaper,
    sports: Volleyball
  };

  return (
    <div 
      className="minimal-project-card" 
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleCardClick}
      tabIndex={0}
      onFocus={() => setIsHovered(true)}
      onBlur={() => setIsHovered(false)}
    >
      {/* Project Image - hidden in minimal, shows on hover */}
      <div className={`project-image ${isHovered ? 'visible' : 'minimal'}`}>
        <img src={project.image || '/default.png'} alt={project.title} />
        <div className="project-overlay">
          <div className="project-links">
            {project.githubUrl && (
              <a 
                href={project.githubUrl} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="project-link"
                onClick={(e) => e.stopPropagation()}
              >
                GitHub
              </a>
            )}
          </div>
        </div>
      </div>
      
      {/* Project Content - always visible, expands on hover */}
      <div className="project-content">
        {project.year && (
          <div className="project-year-badge">
            {project.year}
          </div>
        )}
        {project.subtitle && (
          <div className="project-subtitle">{project.subtitle}</div>
        )}
        <h3>{project.title}</h3>
        
        {/* Description - only shows on hover */}
        <p className={`project-description ${isHovered ? 'visible' : 'hidden'}`}>
          {project.description}
        </p>
        
        {/* Footer - only shows on hover */}
        <div className={`project-footer ${isHovered ? 'visible' : 'hidden'}`}>
          <div className="project-categories">
            {project.categories.map((category, index) => {
              const IconComponent = categoryIcons[category];
              return IconComponent ? (
                <div 
                  key={index} 
                  className="category-icon-wrapper"
                >
                  <IconComponent 
                    size={18} 
                    className="category-icon" 
                  />
                  <div className="tooltip-balloon">
                    {category.toUpperCase()}
                  </div>
                </div>
              ) : null;
            })}
          </div>
          <div className="read-more-overlay">
            <div className="read-more-message">
              <span>Read more</span>
              <ArrowRight size={14} />
            </div>
          </div>
        </div>
      </div>
      
      {/* Expand Arrow Indicator - only show when not hovered */}
      {!isHovered && (
        <div className="card-expand-indicator">
          <ChevronDown size={16} />
        </div>
      )}
    </div>
  );
};

export default MinimalProjectCard;